// Content surfaces (GroundTeam/Support/Theme.swift CardStyle, Views/Components/Cards.swift SectionCard and
// KeyValueRow, DesignSystem/HeroBackground.swift IconBadge): white rounded cards with a navy-tinted hairline and soft
// shadow in light mode, a tonal surface in dark mode.

import type { ElementType, HTMLAttributes, ReactNode, Ref } from 'react';
import { Link } from 'react-router';
import { cn } from './cn';
import { readableForeground, tintStyle } from './color';

export type CardPadding = 'none' | 'sm' | 'md' | 'lg';

const PADDING: Record<CardPadding, string> = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-6',
};

export interface CardProps extends HTMLAttributes<HTMLElement> {
  /** Element to render (section, article, li...). */
  as?: ElementType;
  padding?: CardPadding;
  /** Hover lift for cards that are links or buttons (iOS PressableStyle). */
  interactive?: boolean;
  ref?: Ref<HTMLElement>;
}

export const cardClass = 'rounded-card border border-line bg-surface shadow-card';

export function Card({ as: Tag = 'div', padding = 'md', interactive, className, ref, ...rest }: CardProps) {
  return (
    <Tag
      ref={ref}
      {...rest}
      className={cn(
        cardClass,
        PADDING[padding],
        interactive &&
          'transition-[box-shadow,transform] duration-150 hover:shadow-raised active:scale-[0.99] cursor-pointer',
        className,
      )}
    />
  );
}

/** Uppercase caption above a card or a group (iOS SectionLabel). */
export function SectionLabel({ children, className, id }: { children: ReactNode; className?: string; id?: string }) {
  return (
    <h2 id={id} className={cn('text-xs font-semibold tracking-[0.08em] text-fg-muted uppercase', className)}>
      {children}
    </h2>
  );
}

export interface SectionCardProps {
  title: ReactNode;
  /** Small text under the title row. */
  description?: ReactNode;
  /** Right side of the title row: anything (buttons, menus). Takes precedence over actionLabel. */
  action?: ReactNode;
  /** iOS SectionCard action: an orange caps link, "SEE ALL" by default. Give `onAction` or `actionHref`. */
  actionLabel?: string;
  onAction?: () => void;
  actionHref?: string;
  padding?: CardPadding;
  className?: string;
  bodyClassName?: string;
  children: ReactNode;
}

/** A labelled card: uppercase caption with an optional action, then the card body. */
export function SectionCard({
  title,
  description,
  action,
  actionLabel,
  onAction,
  actionHref,
  padding = 'md',
  className,
  bodyClassName,
  children,
}: SectionCardProps) {
  const linkClass =
    'text-[11px] font-bold tracking-[0.06em] text-primary-text uppercase hover:underline underline-offset-2';
  const label = (actionLabel ?? 'See all').toUpperCase();
  let trailing: ReactNode = action ?? null;
  if (trailing == null && actionHref) {
    trailing = (
      <Link to={actionHref} className={linkClass}>
        {label}
      </Link>
    );
  } else if (trailing == null && onAction) {
    trailing = (
      <button type="button" onClick={onAction} className={linkClass}>
        {label}
      </button>
    );
  }
  return (
    <section className={cn('flex min-w-0 flex-col gap-2', className)}>
      <div className="flex items-baseline justify-between gap-3 px-1">
        <div className="min-w-0">
          <SectionLabel>{title}</SectionLabel>
          {description != null && <p className="mt-0.5 text-xs text-fg-muted">{description}</p>}
        </div>
        {trailing}
      </div>
      <div className={cn(cardClass, PADDING[padding], 'min-w-0', bodyClassName)}>{children}</div>
    </section>
  );
}

export interface IconBadgeProps {
  icon: ReactNode;
  /** Hex color; navy by default (glyph lifts to the text accent in dark mode, like Theme.textAccent). */
  color?: string;
  /** Pixel size of the square (iOS default 40). */
  size?: number;
  className?: string;
}

/** Rounded tinted square with a glyph in the same color (KPI tiles, settings rows, empty states). */
export function IconBadge({ icon, color = '#102A5E', size = 40, className }: IconBadgeProps) {
  const isNavy = color.toUpperCase() === '#102A5E';
  const tint = tintStyle(color);
  return (
    <span
      aria-hidden
      className={cn('inline-flex shrink-0 items-center justify-center', className)}
      style={{
        width: size,
        height: size,
        borderRadius: size * 0.32,
        backgroundColor: tint.backgroundColor,
        color: isNavy ? 'var(--gt-accent)' : readableForeground(color),
        // lucide icons size from font-size via 1em when width/height are not set; set them explicitly.
        ['--gt-icon' as string]: `${Math.round(size * 0.46)}px`,
      }}
    >
      <span className="inline-flex [&_svg]:size-[var(--gt-icon)]">{icon}</span>
    </span>
  );
}

/** "Key   Value" line used in info panels (iOS KeyValueRow): an em dash for empty values. Render inside a <dl>. */
export function KeyValueRow({ label, value, className }: { label: ReactNode; value: ReactNode; className?: string }) {
  const empty = value === null || value === undefined || value === '';
  return (
    <div className={cn('flex items-baseline justify-between gap-4 py-1.5 text-sm', className)}>
      <dt className="shrink-0 text-fg-muted">{label}</dt>
      <dd className={cn('min-w-0 text-right font-medium break-words', empty ? 'text-fg-subtle' : 'text-fg')}>
        {empty ? '—' : value}
      </dd>
    </div>
  );
}
